import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Refresh, Search, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { fetchWallets } from '../../services/wallet';

const WalletsPage = () => {
  const { t } = useTranslation(['wallets', 'common']);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ORDERING_OPTIONS = [
    { value: '-balance', label: t('filters.balanceHighToLow') },
    { value: 'balance', label: t('filters.balanceLowToHigh') },
    { value: '-bonus_balance', label: t('filters.bonusBalanceHighToLow') },
    { value: 'bonus_balance', label: t('filters.bonusBalanceLowToHigh') },
    { value: '-updated_at', label: t('filters.recentlyUpdated') },
    { value: 'updated_at', label: t('filters.oldestUpdated') },
  ];
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filter and sort state
  const [ordering, setOrdering] = useState('-balance'); // Default: balance desc
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load wallets
  const loadWallets = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page,
        page_size: pageSize,
        ordering,
        ...(debouncedSearch && { search: debouncedSearch }),
      };
      
      const response = await fetchWallets(params);
      
      setWallets(response.results || []);
      setTotalCount(response.count || 0);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('messages.failedToLoad'));
      console.error('Error loading wallets:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, ordering, debouncedSearch]);

  useEffect(() => {
    loadWallets();
  }, [loadWallets]);

  const handleOrderingChange = (event) => {
    setOrdering(event.target.value);
    setPage(1); // Reset to first page on sort change
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page on page size change
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOrderingIcon = (field) => {
    if (ordering === field) {
      return <ArrowDownward fontSize="small" />;
    }
    if (ordering === `-${field}`) {
      return <ArrowUpward fontSize="small" />;
    }
    return null;
  };

  const handleSort = (field) => {
    if (ordering === field) {
      setOrdering(`-${field}`);
    } else if (ordering === `-${field}`) {
      setOrdering(field);
    } else {
      setOrdering(`-${field}`); // Default to desc
    }
    setPage(1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        {t('title')}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('summary.totalWallets')}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {totalCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('summary.totalBalance')}
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {formatCurrency(
                  wallets.reduce((sum, w) => sum + parseFloat(w.balance || 0), 0)
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('summary.totalBonusBalance')}
              </Typography>
              <Typography variant="h6" fontWeight={600} color="secondary">
                {formatCurrency(
                  wallets.reduce((sum, w) => sum + parseFloat(w.bonus_balance || 0), 0)
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('summary.currentPage')}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {page} / {totalPages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label={t('search.label')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('search.placeholder')}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>{t('filters.sortBy')}</InputLabel>
              <Select
                value={ordering}
                onChange={handleOrderingChange}
                label={t('filters.sortBy')}
              >
                {ORDERING_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('filters.perPage')}</InputLabel>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                label={t('filters.perPage')}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title={t('tooltips.refresh')}>
              <IconButton onClick={loadWallets} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Chip label={error} color="error" onDelete={() => setError(null)} />
        </Box>
      )}

      {/* Wallets Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={ordering === 'balance' || ordering === '-balance'}
                          direction={ordering === '-balance' ? 'desc' : 'asc'}
                          onClick={() => handleSort('balance')}
                          IconComponent={() => getOrderingIcon('balance')}
                        >
                          {t('table.user')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={ordering === 'balance' || ordering === '-balance'}
                          direction={ordering === '-balance' ? 'desc' : 'asc'}
                          onClick={() => handleSort('balance')}
                          IconComponent={() => getOrderingIcon('balance')}
                        >
                          {t('table.balance')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={ordering === 'bonus_balance' || ordering === '-bonus_balance'}
                          direction={ordering === '-bonus_balance' ? 'desc' : 'asc'}
                          onClick={() => handleSort('bonus_balance')}
                          IconComponent={() => getOrderingIcon('bonus_balance')}
                        >
                          {t('table.bonusBalance')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={ordering === 'updated_at' || ordering === '-updated_at'}
                          direction={ordering === '-updated_at' ? 'desc' : 'asc'}
                          onClick={() => handleSort('updated_at')}
                          IconComponent={() => getOrderingIcon('updated_at')}
                        >
                          {t('table.lastUpdated')}
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wallets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {debouncedSearch ? t('table.noWalletsMatching') : t('table.noWalletsFound')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      wallets.map((wallet) => (
                        <TableRow key={wallet.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {wallet.user_fullname || wallet.user_name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {wallet.user_email || 'N/A'}
                              </Typography>
                              {wallet.user_phone && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {wallet.user_phone}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {formatCurrency(wallet.balance)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="secondary">
                              {formatCurrency(wallet.bonus_balance)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(wallet.updated_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 3 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('pagination.showing')} {((page - 1) * pageSize) + 1} {t('pagination.to')} {Math.min(page * pageSize, totalCount)} {t('pagination.of')} {totalCount} {t('pagination.wallets')}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePrevPage}
                      disabled={page === 1 || loading}
                      startIcon={<ArrowUpward />}
                    >
                      {t('pagination.previous')}
                    </Button>
                    <Typography variant="body2" sx={{ px: 2 }}>
                      {t('pagination.page')} {page} {t('pagination.of')} {totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleNextPage}
                      disabled={page >= totalPages || loading}
                      endIcon={<ArrowDownward />}
                    >
                      {t('pagination.next')}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WalletsPage;

